import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  HStack,
  VStack,
  Text,
  Image,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Input,
  SimpleGrid,
  Spinner,
  AspectRatio,
  Link,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import axios from "axios";
import { apiUrl } from "../config";

const PexelsSearchModal = ({ isOpen, onClose, onPick, defaultQuery = "" }) => {
  const [query, setQuery] = useState(defaultQuery);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setQuery(defaultQuery);
    }
  }, [isOpen, defaultQuery]);

  const runSearch = async (e) => {
    e?.preventDefault?.();
    // The modal portals into <body>, but React synthetic events bubble through
    // the React tree — without this, the inner submit reaches the outer
    // Formik <Form> and submits the whole business form, closing the modal.
    e?.stopPropagation?.();
    const q = query.trim();
    if (!q) return;
    setLoading(true);
    setError(null);
    try {
      const idToken = sessionStorage.getItem("idToken");
      const res = await axios.get(`${apiUrl}/pexels/search`, {
        params: { q, per_page: 18 },
        headers: { Authorization: `Bearer ${idToken}` },
      });
      setPhotos(res.data.photos || []);
    } catch (err) {
      console.error("Pexels search failed", err);
      if (err.response?.status === 503) {
        setError("Pexels is not configured on this environment yet.");
      } else {
        setError(
          err.response?.data?.error ||
            "Search failed. Please try again in a moment."
        );
      }
      setPhotos([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setQuery(defaultQuery);
    setPhotos([]);
    setError(null);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="3xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Choose a photo from Pexels</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            <form onSubmit={runSearch}>
              <HStack>
                <Input
                  placeholder="Search Pexels (e.g., 'main street', 'community')"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  autoFocus
                />
                <Button
                  type="submit"
                  isLoading={loading}
                  leftIcon={<SearchIcon />}
                  variant="outline"
                >
                  Search
                </Button>
              </HStack>
            </form>

            {error && (
              <Text color="red.500" fontSize="sm">
                {error}
              </Text>
            )}

            {loading ? (
              <Box textAlign="center" py={8}>
                <Spinner />
              </Box>
            ) : photos.length > 0 ? (
              <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3}>
                {photos.map((p) => (
                  <Box
                    key={p.id}
                    as="button"
                    type="button"
                    onClick={() => onPick(p)}
                    borderRadius="md"
                    overflow="hidden"
                    borderWidth="1px"
                    borderColor="gray.200"
                    _hover={{ borderColor: "blue.400", boxShadow: "md" }}
                    cursor="pointer"
                  >
                    <AspectRatio ratio={16 / 10}>
                      <Image
                        src={p.previewUrl}
                        alt={`Photo by ${p.photographer}`}
                        objectFit="cover"
                      />
                    </AspectRatio>
                    <Text
                      fontSize="xs"
                      color="gray.600"
                      px={2}
                      py={1}
                      textAlign="left"
                      noOfLines={1}
                    >
                      by {p.photographer}
                    </Text>
                  </Box>
                ))}
              </SimpleGrid>
            ) : (
              <Text fontSize="sm" color="gray.500" textAlign="center" py={6}>
                Type a search term and press Enter.
              </Text>
            )}

            <Text fontSize="xs" color="gray.500">
              Photos provided by{" "}
              <Link href="https://www.pexels.com" isExternal color="blue.600">
                Pexels
              </Link>
              .
            </Text>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PexelsSearchModal;
